import React, { useState, useEffect } from 'react';
import { Box, Avatar, Typography, Menu, MenuItem } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/auth.service';

/**
 * UserProfile Component
 *
 * Displays user avatar, name, and logout button in the top right corner
 */
const UserProfile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Debug: log user object to see what fields are available
  useEffect(() => {
    if (user) {
      // eslint-disable-next-line no-console
      console.log('🔍 UserProfile - User object:', user);
      // eslint-disable-next-line no-console
      console.log('🔍 UserProfile - Picture URL:', user?.picture);
    }
  }, [user]);

  if (!user) {
    return null;
  }

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      // Get tenant alias before logout to preserve it for login redirect
      const tenant = authService.getTenant();
      const tenantAlias = tenant
        ? typeof tenant === 'string'
          ? tenant
          : tenant.alias || tenant.tenant_id
        : null;

      await logout();

      // Navigate to login with tenant alias if available
      if (tenantAlias) {
        navigate(`/login?tenant=${encodeURIComponent(tenantAlias)}`);
      } else {
        navigate('/login');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', error);
    }
  };

  // Get user display name (email or name field)
  const displayName = user.name || user.email || 'User';

  // Get avatar URL (Google picture or default)
  // Handle both string URLs and ensure proper format
  const avatarUrl = user.picture ? String(user.picture).trim() : null;

  // Generate user initials for avatar fallback
  const getInitials = name => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      // First letter of first name + first letter of last name
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    } else if (parts.length === 1) {
      // Single name - take first two characters
      return parts[0].substring(0, 2).toUpperCase();
    }
    // Fallback: use email first letter if available
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  const userInitials = getInitials(displayName);

  // Generate a consistent color based on user's name/email
  const getAvatarColor = str => {
    if (!str) return 'primary.main';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Generate a color from the hash
    const hue = hash % 360;
    return `hsl(${hue}, 65%, 50%)`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* User Info */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
        onClick={handleClick}
      >
        <Avatar
          src={avatarUrl}
          alt={displayName}
          imgProps={{
            crossOrigin: 'anonymous',
            referrerPolicy: 'no-referrer',
            onError: e => {
              // eslint-disable-next-line no-console
              console.warn('Avatar image failed to load:', avatarUrl);
              // Hide the img element, show the fallback initials
              e.target.style.display = 'none';
            },
          }}
          sx={{
            width: 36,
            height: 36,
            border: '2px solid #fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            bgcolor: avatarUrl ? 'transparent' : getAvatarColor(displayName),
            color: avatarUrl ? 'inherit' : '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {!avatarUrl && userInitials}
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {displayName}
        </Typography>
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 1.5, fontSize: 20 }} />
          <Typography variant="body2">Log Out</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserProfile;
